import { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Send, X, ArrowRight, Loader2, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/money";

function previousQuarterOf(date = new Date()) {
  const y = date.getFullYear();
  const q = Math.floor(date.getMonth() / 3) + 1;
  if (q === 1) return { year: y - 1, quarter: 4 };
  return { year: y, quarter: q - 1 };
}

function dismissKey(y, q) { return `claimbook_dismiss_${y}_${q}`; }

export default function SubmitPreviousQuarterBanner({ summary, onSelectQuarter, onSubmitted }) {
  const prev = useMemo(() => previousQuarterOf(new Date()), []);
  const prevData = useMemo(() => {
    if (!summary?.quarters) return null;
    return summary.quarters.find((q) => q.year === prev.year && q.quarter === prev.quarter) || null;
  }, [summary, prev]);

  const [dismissed, setDismissed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setDismissed(localStorage.getItem(dismissKey(prev.year, prev.quarter)) === "1");
  }, [prev.year, prev.quarter]);

  if (!prevData) return null;
  if (prevData.status !== "open") return null;
  if ((prevData.count || 0) === 0) return null;
  if (dismissed) return null;

  const dismiss = () => {
    localStorage.setItem(dismissKey(prev.year, prev.quarter), "1");
    setDismissed(true);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/quarters/${prev.year}/${prev.quarter}/status`, { status: "submitted" });
      toast.success(`Q${prev.quarter} ${prev.year} marked as submitted`);
      await onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const totalStr = formatMoney(prevData.total);

  return (
    <AnimatePresence>
      <motion.div
        key={`prev-q-nudge-${prev.year}-${prev.quarter}`}
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.35 }}
        data-testid="submit-previous-quarter-banner"
      >
        <Card className="relative p-5 md:p-6 border border-primary/30 bg-primary text-primary-foreground overflow-hidden">
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
            <div className="w-11 h-11 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 flex items-center justify-center shrink-0">
              <CalendarClock className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-primary-foreground/70">
                Ready to submit
              </div>
              <div className="mt-1 font-display text-lg md:text-xl font-semibold tracking-tight leading-tight">
                Q{prev.quarter} {prev.year} is done — <span className="tabular-nums">{totalStr}</span> across {prevData.count} expense{prevData.count === 1 ? "" : "s"}
              </div>
              <div className="mt-1 text-sm text-primary-foreground/75">
                Send it to your company now so you don&apos;t forget.
              </div>
            </div>
            <div className="flex flex-wrap gap-2 md:justify-end">
              <Button
                onClick={submit}
                disabled={submitting}
                variant="secondary"
                className="rounded-full h-9 gap-2 bg-primary-foreground text-primary hover:bg-primary-foreground/90"
                data-testid="nudge-submit-now-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Submit now
              </Button>
              <Button
                onClick={() => onSelectQuarter?.(prev)}
                variant="ghost"
                className="rounded-full h-9 gap-1 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                data-testid="nudge-review-btn"
              >
                Review <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="absolute top-2 right-2 rounded-full p-1.5 text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10 transition-colors"
            aria-label="Dismiss"
            data-testid="nudge-dismiss-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
}
